import React, { useState, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { type OptionGroups, TableFilter } from '../TableFilter';
import { type DropDownSelectOption } from '../Select';
import { COMMON_FILTER_TEXT } from '../../pages';

export type FilterPanelProps = {
  filterOptionGroups: OptionGroups[];
  initialAppliedFilter: any;
  setFilter: (filter: any, appliedFilter: any) => void;
};

export const FilterPanel: React.FC<FilterPanelProps> = props => {
  const {
    initialAppliedFilter,
    setFilter: setParentFilter,
    filterOptionGroups,
  } = props;

  const [expandFilter, setExpandFilter] = useState<boolean>(true);
  const handleOpenFilter = (): void => {
    setExpandFilter(!expandFilter);
  };

  const [appliedFilter, setAppliedFilter] = useState<string[][]>(
    initialAppliedFilter ??
      Array.from({ length: filterOptionGroups.length }, () => []),
  );

  const handleApplySelectedFilterOptions = (
    selectedOptionGroupsArr: string[][],
  ): any => {
    setAppliedFilter(selectedOptionGroupsArr);
    // pass to external
    let filter = {};
    selectedOptionGroupsArr.forEach((selectedOptionGroups, index) => {
      if (selectedOptionGroups.length && selectedOptionGroups[0] !== '') {
        const fieldName = filterOptionGroups[index]?.fieldName;
        if (fieldName == null) return;

        const appendFilter = (
          filter: any,
          fieldName: string,
          index: number,
        ): any => {
          if (filterOptionGroups[index]?.fieldType === 'boolean') {
            if (selectedOptionGroups.length === 1) {
              filter = {
                ...filter,
                [fieldName]: selectedOptionGroups[0] === 'true',
              };
            }

            // else if the length of the boolean arr > 1
            // which means it contains true and false, no need to make any change
          } else if (filterOptionGroups[index]?.fieldType === 'object') {
            if (selectedOptionGroups.length === 1) {
              filter = {
                ...filter,
                [fieldName]: JSON.parse(selectedOptionGroups[0]),
              };
            }
          } else if (filterOptionGroups[index]?.isFuzzy) {
            filter = {
              ...filter,
              [fieldName]: {
                regex: `.*?${selectedOptionGroups[0]}.*?`,
              },
            };
          } else {
            filter = {
              ...filter,
              [fieldName]: {
                in: selectedOptionGroups,
              },
            };
          }
          return filter;
        };

        if (!fieldName.includes('.')) {
          filter = appendFilter(filter, fieldName, index);
        } else {
          // nested filter case
          const hierarchy = fieldName.split('.');
          let lastFilter = appendFilter(
            {},
            hierarchy[hierarchy.length - 1],
            index,
          );
          for (let i = hierarchy.length - 2; i >= 0; i--) {
            lastFilter = { [hierarchy[i]]: lastFilter };
          }
          filter = DeepMerge(filter, lastFilter);
        }
      }
    });
    return filter;
  };

  const getFilteringConditionText = (): string => {
    const conditionText: string = appliedFilter
      ?.map((selectedFilterOption, index) => {
        if (selectedFilterOption?.length && selectedFilterOption?.[0] !== '') {
          // map back the label
          return `${filterOptionGroups?.[index]?.title}: ${selectedFilterOption
            ?.map(selectedOption => {
              if (filterOptionGroups?.[index]?.displayLabel === true) {
                const option = filterOptionGroups?.[index]?.options?.find(
                  (option: DropDownSelectOption) =>
                    option.value === selectedOption,
                );
                return option?.label ?? selectedOption;
              }
              return selectedOption;
            })
            .join(', ')}`;
        }
        return null;
      })
      .filter(item => item !== null)
      .join(', ');

    return conditionText === ''
      ? '[Selected: All]'
      : `[Selected Conditions: ${conditionText}]`;
  };

  useEffect(() => {
    const filter = handleApplySelectedFilterOptions(appliedFilter);
    setParentFilter(filter, appliedFilter);
  }, [appliedFilter]);

  return (
    <>
      <Accordion className="mb-4" expanded={expandFilter}>
        <AccordionSummary
          onClick={handleOpenFilter}
          style={{
            background: '#F1F1F1',
            color: '#757070',
          }}
        >
          <div className="flex flex-col w-full">
            <div className="flex items-start">
              {expandFilter ? (
                <ExpandLess
                  style={{
                    transform: 'rotate(180deg)',
                    color: 'grey',
                    marginRight: 10,
                  }}
                />
              ) : (
                <ExpandMore
                  style={{
                    transform: 'rotate(270deg)',
                    color: 'grey',
                    marginRight: 10,
                  }}
                />
              )}
              <strong>{COMMON_FILTER_TEXT.TITLE}</strong>
            </div>
            <div className="flex flex-row justify-between">
              <i
                className="overflow-ellipsis overflow-hidden whitespace-nowrap pl-8 text-sm"
                title={getFilteringConditionText()}
              >
                {getFilteringConditionText()}
              </i>
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails className="bg-[#F1F1F1]">
          <TableFilter
            optionGroups={filterOptionGroups}
            appliedSelection={appliedFilter}
            onApply={handleApplySelectedFilterOptions}
          />
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export const DeepMerge = (target: any, source: any): any => {
  const isObject = (item: any): boolean => {
    return Boolean(item) && typeof item === 'object' && !Array.isArray(item);
  };

  const output: any = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, { [key]: source[key] });
        else output[key] = DeepMerge(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};
